import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import HomePage from "./components/pages/HomePage";
import ChatWidget from "./ChatWidget";


// Lazy load non-critical pages for better performance
const AboutPage = lazy(() => import("./components/pages/AboutPage"));
const PricingPage = lazy(() => import("./components/pages/PricingPage"));
const ContactPage = lazy(() => import("./components/pages/ContactPage"));
const PrivacyPolicyPage = lazy(
  () => import("./components/pages/PrivacyPolicyPage"),
);
const VideoPage = lazy(() => import("./components/pages/VideoPage"));
const NotFoundPage = lazy(() => import("./components/pages/NotFoundPage"));
const BlogPage = lazy(() => import("./components/pages/BlogPage"));
const NewsPage = lazy(() => import("./components/pages/NewsPage"));
const StoriesPage = lazy(() => import("./components/pages/StoriesPage"));
const BVKGroupStoryPage = lazy(() =>
  import("./components/pages/StoryDetailsPage").then((module) => ({
    default: module.BVKGroupStoryPage,
  })),
);
const DemoPage = lazy(() => import("./components/pages/DemoPage"));
const FleetManagementPage = lazy(
  () => import("./components/pages/services/FleetManagementPage"),
);
const GPSTrackingPage = lazy(
  () => import("./components/pages/services/GPSTrackingPage"),
);
const GeofencingPage = lazy(
  () => import("./components/pages/services/GeofencingPage"),
);
const LocationIntelligencePage = lazy(
  () => import("./components/pages/services/LocationIntelligencePage"),
);
const ApiSdkPage = lazy(() => import("./components/pages/services/ApiSdkPage"));
const RedirectToApp = lazy(() => import("./components/pages/RedirectPage"));
const DevelopersPage = lazy(() => import("./components/pages/DevelopersPage"));
const LogisticsPage = lazy(
  () => import("./components/pages/sectors/LogisticsPage"),
);
const LandingPage = lazy(
  () => import("./components/pages/landingPage/LandingPage"),
);
const QRScanPage = lazy(() => import("./components/pages/QRScanPage"));
const GeotagLandingPage = lazy(() => import("./components/pages/GeotagLandingPage"));
const GeotagPage = lazy(() => import("./components/pages/services/GeotagPage"));
const JustAroundPage = lazy(() => import("./components/pages/JustAroundPage"));

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
    }}
  >
    <div>Loading...</div>
  </div>
);

// Main Layout Component for Standard Pages
const MainLayout = () => {
  return (
    <div className="main-wrapper">
      <Header />
      <Outlet />
      <Footer />
      <ChatWidget />
    </div>
  );
};

function App() {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Standalone Pages (No Header/Footer) */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/geotag-landing" element={<GeotagLandingPage />} />
          <Route path="/geotag/:code" element={<QRScanPage />} />

          {/* Main Application Routes - Wrapped in Layout */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route
              path="/service/fleet-management"
              element={<FleetManagementPage />}
            />
            <Route path="/service/gps-tracking" element={<GPSTrackingPage />} />
            <Route path="/service/geofencing" element={<GeofencingPage />} />
            <Route
              path="/service/location-intelligence"
              element={<LocationIntelligencePage />}
            />
            <Route path="/service/api-sdk" element={<ApiSdkPage />} />
            <Route path="/service/geotag" element={<GeotagPage />} />
            <Route path="/product/justaround" element={<JustAroundPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/video" element={<VideoPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/blogs" element={<BlogPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/stories/bvk-group" element={<BVKGroupStoryPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/redirect" element={<RedirectToApp />} />
            <Route path="/developers" element={<DevelopersPage />} />
            <Route path="/logistics" element={<LogisticsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
